import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  /** JWT `sub` claim — the user UUID. */
  sub: string;
  /** JWT `tenant_id` claim. */
  tenantId: string;
  /** JWT `role` claim (owner | admin | engineer | viewer). */
  role: string;
  email?: string;
  /** Unix seconds expiry, if present. */
  exp?: number;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setToken: (token: string) => void;
  clear: () => void;
}

/** Decode a JWT payload (no signature verification — display/claims only). */
export function decodeJwt(token: string): AuthUser | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = decodeURIComponent(
      json
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const claims = JSON.parse(decoded) as Record<string, unknown>;
    return {
      sub: typeof claims.sub === 'string' ? claims.sub : '',
      tenantId: typeof claims.tenant_id === 'string' ? claims.tenant_id : '',
      role: typeof claims.role === 'string' ? claims.role : 'viewer',
      email: typeof claims.email === 'string' ? claims.email : undefined,
      exp: typeof claims.exp === 'number' ? claims.exp : undefined,
    };
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setToken: (token: string) => set({ token, user: decodeJwt(token) }),
      clear: () => set({ token: null, user: null }),
    }),
    { name: 'blazeipam.auth' },
  ),
);

/** True when a token exists and (if it carries an exp) has not expired. */
export function isAuthenticated(): boolean {
  const { token, user } = useAuthStore.getState();
  if (!token) return false;
  if (user?.exp && user.exp * 1000 < Date.now()) return false;
  return true;
}

// Non-React accessors for the api-client (which is not a hook).
export function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

export function clearAuth(): void {
  useAuthStore.getState().clear();
}
